import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import { Type } from '@warp-drive/core-types/symbols';
import type { SupportedCurrencies } from 'frontend-utils/utils/constants/currency-mapping-constants';
import type { SmartInsightDay } from 'lumen/models';
import {
  SMART_INSIGHT_DAY_ENTRY_TYPE,
  type SmartInsightEntryType,
} from 'lumen/models/smart-insights/smart-insight-day-insight-entry';

const TEMPLATE_RATES_RECO_RATE_DECREASE = 'RECO_RATE_DECREASE';

export default class SmartInsightDayRecommendationEntryModel extends Model {
  [Type] = 'smart-insights/smart-insight-day-recommendation-entry' as const;

  @belongsTo('smart-insights/smart-insight-day', {
    async: false,
    inverse: 'recommendationEntries',
  })
  declare smartInsightDay: SmartInsightDay;

  @attr() declare config: { currency: SupportedCurrencies };
  @attr() declare info: {
    recommendation_element_type?: SmartInsightEntryType;
    text: {
      template: string;
      template_key: string;
      variables: Record<string, string>;
    };
  };

  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];

  @cached
  get formattedText(): string {
    const { template } = this.info.text;
    const processedVariables = this.processVariables();

    try {
      return this.intl.formatMessage(template, processedVariables);
    } catch (e) {
      this.errorTracking.captureMessage(
        'Smart Insights: recommendation template formatting failed',
        {
          templateKey: this.info.text.template_key,
          recommendationElementType: this.info.recommendation_element_type,
          template,
          processedVariables,
          error: e instanceof Error ? e.message : String(e),
        },
      );
      return template;
    }
  }

  get iconName(): IconName {
    switch (this.info.recommendation_element_type) {
      case SMART_INSIGHT_DAY_ENTRY_TYPE.RATES: {
        const templateKey = this.info.text.template_key;

        if (templateKey === TEMPLATE_RATES_RECO_RATE_DECREASE) {
          return 'arrow-down';
        }

        return 'arrow-up';
      }
      case SMART_INSIGHT_DAY_ENTRY_TYPE.LOS_RESTRICTIONS:
        return 'calendar-clock';
      default:
        return 'smart-insights';
    }
  }

  private processVariableCurrency(
    key: string,
    value: string,
    processedVariables: Record<string, string | number>,
  ): void {
    const valueAsNumber = Number(value);

    if (value !== '' && !isNaN(valueAsNumber)) {
      const formatNumberOptions = {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        ...(this.config.currency && {
          style: 'currency' as const,
          currency: this.config.currency,
          currencyDisplay: 'symbol' as const,
        }),
      };

      processedVariables[key] = this.intl.formatNumber(
        valueAsNumber,
        formatNumberOptions,
      );
    } else {
      processedVariables[key] = value;
    }
  }

  private processVariableCurrencyWithSign(
    key: string,
    value: string,
    processedVariables: Record<string, string | number>,
  ): void {
    const valueAsNumber = Number(value);

    if (value !== '' && !isNaN(valueAsNumber)) {
      const formatNumberOptions = {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        signDisplay: 'always' as const,
        ...(this.config.currency && {
          style: 'currency' as const,
          currency: this.config.currency,
          currencyDisplay: 'symbol' as const,
        }),
      };

      processedVariables[key] = this.intl.formatNumber(
        valueAsNumber,
        formatNumberOptions,
      );
    } else {
      processedVariables[key] = value;
    }
  }

  private processVariableNumber(
    key: string,
    value: string,
    processedVariables: Record<string, string | number>,
  ): void {
    const valueAsNumber = Number(value);

    if (value !== '' && !isNaN(valueAsNumber)) {
      processedVariables[key] = this.intl.formatNumber(valueAsNumber, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    } else {
      processedVariables[key] = value;
    }
  }

  private processVariables(): Record<string, string | number> {
    const processedVariables: Record<string, string | number> = {};

    switch (this.info.recommendation_element_type) {
      case SMART_INSIGHT_DAY_ENTRY_TYPE.RATES:
        Object.entries(this.info.text.variables).forEach(([key, value]) => {
          if (key === 'diff') {
            this.processVariableCurrencyWithSign(
              key,
              value,
              processedVariables,
            );
          } else {
            this.processVariableCurrency(key, value, processedVariables);
          }
        });
        break;
      case SMART_INSIGHT_DAY_ENTRY_TYPE.LOS_RESTRICTIONS:
        Object.entries(this.info.text.variables).forEach(([key, value]) => {
          this.processVariableNumber(key, value, processedVariables);
        });
        break;
      default:
        Object.entries(this.info.text.variables).forEach(([key, value]) => {
          this.processVariableCurrency(key, value, processedVariables);
        });
        break;
    }

    return processedVariables;
  }
}
